<template>
  <div data-view="banners">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame :title="title">
      <template v-slot:header-actions>
        <Can action="store" subject="banner">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'site-messages-new', query: $route.query })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_banner') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:content>
        <BannersFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="banners"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectBanner"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'countries'">
              <div>
                {{
                  displayCountries(scope.cell.item)
                }}
              </div>
            </template>

            <template v-else-if="scope.cell.column.value !== 'status.name'">
              <div>
                {{
                  displayColumnValue(scope.cell.item, scope.cell.column.value)
                }}
              </div>
            </template>

            <template v-else>
              <StatusPanel
                table-group="banners"
                :status="scope.cell.item.status.name"
              />
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import BannersFilters from '../../components/forms/filters/SiteMessages';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import StatusPanel from '../../components/panels/Status';
  import {extractNestedProp, isStr} from '@/utils';
  import columns from '../../config/siteMessages/columns';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      BannersFilters,
      StatusPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: '',
        },
        query: { per_page: 15 },
        totalPages: 0,
        isLoading: false,
      };
    },
    computed: {
      title() {
        const amount = this.totalCount > 0 ? `(${this.totalCount})` : '';
        return this.$t('site_messages') + ' ' + amount;
      },
      banners() {
        return this.$store.getters['siteMessages/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getBanners(this.query);
      },
      getBanners(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('siteMessages/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['siteMessages/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectBanner(banner) {
        this.$store.dispatch('siteMessages/setActiveItem', banner);
        this.$router.push({ name: 'site-messages-edit', params: { id: banner.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.runInitialFetch(() => this.getBanners(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getBanners(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getBanners(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };

        this.getBanners(this.query);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayCountries(columnValue) {
        return columnValue.countries.map(el => el.name).join(', ');
      },
    },
  };
</script>
